import { link } from "../App";
import { useEffect, useMemo, useState } from "react";
import {
  MAX_PLAINTEXT_BYTES,
  MAX_PRIVATE_PLAINTEXT_BYTES,
  MAX_ROUND,
  manifestBytes,
  publicKeyToNpub,
  roundForTime,
  sealPrivate,
  sealPrivateWithCopy,
  sealPrivateGroup,
  openAddressBook,
  type AddressBook,
  sealPublic,
  normalizeSubject,
  MAX_SUBJECT_CHARS,
  normalizeIntro,
  MAX_INTRO_CHARS,
  timeForRound,
  cleanFileName,
  type Event,
  type ManifestFile,
} from "@timecapsule/crypto";
import { formatBytes, prepareFile, uploadFile, type Prepared } from "../attachments";
import { api, ApiError } from "../api";
import { estimateStamps, knownShortage } from "../billing";
import { chainClient, explain } from "../capsules";
import { navigate, type ReplyTarget } from "../router";
import { isKeyRequestCancelled, useSession } from "../session";
import { seenKeys, sentPrivate, type SeenKey, type SentPrivate } from "../storage";
import { fingerprint } from "../keys";
import { errorText, useLang, useT } from "../i18n";
import { formatNumber, formatTime, useNow } from "../time";
import { Disclosure } from "../Disclosure";
import { Icon } from "../Icon";
import { draftKey, dropDraft, hasContent, loadDraft, saveDraft } from "../drafts";
import { announceSealed, type Sealed } from "../sealed";

const PRESETS = [
  ["m5", 5 * 60_000],
  ["h1", 3_600_000],
  ["d1", 86_400_000],
  ["w1", 7 * 86_400_000],
  ["y1", 365 * 86_400_000],
] as const;

/** Latest unlock time the crypto package accepts (PLAN.md 4.11 H7). */
const LATEST_UNLOCK = timeForRound(MAX_ROUND);

/**
 * A capsule built and signed, kept until the server confirms it (PLAN.md 4.11 H6). "Send again"
 * resends exactly these events, which the server stores by id, so a retry never duplicates.
 */
/** `group`: wraps to several members (and maybe a copy as the last event), sent through /api/messages/group. */
/** Recipients of one group send; the server enforces the same limit. */
const MAX_GROUP = 20;

type Outgoing = { events: Event[]; note: SentPrivate | null; blobs: string[]; round: number; group?: { copy: boolean }; newcomer?: string };

/** A manifest entry with the right field lengths, to count manifest bytes before encrypting. */
const placeholderEntry = (p: Prepared): ManifestFile => ({
  name: p.name,
  type: p.type,
  size: p.bytes.length,
  sha256: "0".repeat(64),
  key: "A".repeat(43) + "=",
  nonce: "A".repeat(16),
});

/** Value for <input type="datetime-local"> in the browser's time zone. */
function toLocalInput(ms: number): string {
  return new Date(ms - new Date(ms).getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/**
 * `reply` (PLAN.md 4.21): Write opened from Correspondence to answer a request with a reply
 * envelope: the recipient is the sender of that request (follow or not) and the opening time is
 * the envelope's when it set one.
 */
export function Compose({ recipientIds = [], reply = null }: { recipientIds?: number[]; reply?: ReplyTarget | null }) {
  const t = useT();
  const { lang } = useLang();
  // Private capsules go only to members I follow and to myself (PLAN.md 4.14): `reachable` is exactly them.
  const { me, keyStatus, reachable: members, nameOf, reloadFollows, withKey, followed, refresh, blockedBy, memberByKey, rememberMembers } = useSession();
  // The member who asked for a reply: found by the key that signed the request, looked up afresh
  // every time (their active key may have changed since the session cached them; 4.21 review,
  // finding 5). The reply goes to their active key.
  const [replyLookup, setReplyLookup] = useState<"pending" | "done" | "failed">(reply ? "pending" : "done");
  useEffect(() => {
    if (!reply) return;
    let cancelled = false;
    api.lookupMembers([], [reply.authorKey]).then((list) => { if (cancelled) return; rememberMembers(list); setReplyLookup("done"); }, () => { if (!cancelled) setReplyLookup("failed"); });
    return () => { cancelled = true; };
  }, [reply, rememberMembers]);
  const replyMember = reply && replyLookup !== "pending" ? memberByKey(reply.authorKey) : undefined;
  // PLAN.md 4.16 K9: a member who blocks me gets no private capsule from me; Write says so before sending.
  const blocksMe = (pubkey: string) => { const member = members.find((m) => m.pubkey === pubkey); return !!member && blockedBy.has(member.id); };
  const now = useNow(1000);
  // Follows are fetched again whenever Write opens: a followed member may have a new key since the page loaded.
  useEffect(() => {
    void reloadFollows();
  }, [reloadFollows]);
  // An unsent draft of this Write entry survives a trip to another page (PLAN.md 4.29).
  const draft = draftKey(me.id, recipientIds, reply?.id ?? null);
  const presetSubject = reply?.subject ? `Re: ${reply.subject}` : "";
  const [restored, setRestored] = useState(() => loadDraft(draft) !== undefined);
  const [text, setText] = useState(() => loadDraft(draft)?.text ?? "");
  const [subject, setSubject] = useState(() => loadDraft(draft)?.subject ?? presetSubject);
  const [intro, setIntro] = useState(() => loadDraft(draft)?.intro ?? "");
  const [preset, setPreset] = useState<number | "custom">(PRESETS[1][1]);
  const [custom, setCustom] = useState(() => toLocalInput(Date.now() + 86_400_000));
  const targets = recipientIds.map((id) => members.find((m) => m.id === id && m.pubkey && (id === me.id || followed.has(id)))?.pubkey).filter((pk): pk is string => !!pk);
  const targetAvailable = targets.length === recipientIds.length;
  const [chosen, setRecipient] = useState(() => recipientIds.length === 1 && targetAvailable ? targets[0] : "");
  // In reply mode the recipient is the request sender's active key, nothing to choose.
  const recipient = reply ? (replyMember?.pubkey ?? "") : chosen;
  // CC (PLAN.md 4.21): more recipients of the same capsule, who all see each other's names inside the seal.
  const [cc, setCc] = useState<string[]>([]);
  // Reply envelope (PLAN.md 4.21): ask for a reply that opens at a time I set, or at the recipient's choice.
  const [askReply, setAskReply] = useState(false);
  const [replyFixed, setReplyFixed] = useState(true);
  const [replyCustom, setReplyCustom] = useState(() => toLocalInput(Date.now() + 7 * 86_400_000));
  // Several members at once (PLAN.md 4.14): their pubkeys; null while sending to everyone or to one member.
  const [many, setMany] = useState<string[] | null>(() => recipientIds.length > 0 && (recipientIds.length > 1 || !targetAvailable) ? (targetAvailable ? targets : []) : null);
  const [book, setBook] = useState<AddressBook | null>(null);
  const [manyKeysChecked, setManyKeysChecked] = useState(false);
  // Someone who is not a member yet (PLAN.md 4.14): their address; null otherwise.
  const [newcomer, setNewcomer] = useState<string | null>(null);
  const [changedMany, setChangedMany] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [keepCopy, setKeepCopy] = useState(true);
  // Discreet (PLAN.md 4.16 K10): the recipient's lists hide my name until they click the capsule.
  const [discreet, setDiscreet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The server's own answer about stamps, storage or file size: say so and point to Billing.
  const [billingHint, setBillingHint] = useState(false);
  const [outgoing, setOutgoing] = useState<Outgoing | null>(null);
  // A group send that reached everyone except members who block me: say who, then go on to Sent.
  const [leftOut, setLeftOut] = useState<string[] | null>(null);
  // A CC send with members left out: the sealed list still names them (PLAN.md 4.21 review).
  const [leftOutOfCc, setLeftOutOfCc] = useState(false);
  const [attached, setAttached] = useState<Prepared[]>(() => loadDraft(draft)?.attached ?? []);
  const [preparing, setPreparing] = useState(false);
  useEffect(() => saveDraft(draft, { subject, intro, text, attached }, presetSubject), [draft, subject, intro, text, attached, presetSubject]);
  const drafted = hasContent({ subject, intro, text, attached }, presetSubject);
  function discardDraft() {
    if (!confirm(t.compose.draftDiscardConfirm)) return;
    setText("");
    setSubject(presetSubject);
    setIntro("");
    setAttached([]);
    setRestored(false);
    dropDraft(draft);
  }
  // Any change makes it a different capsule.
  useEffect(() => setOutgoing(null), [text, subject, intro, recipient, many, newcomer, preset, custom, keepCopy, discreet, attached, cc, askReply, replyFixed, replyCustom]);

  // The limit measures the manifest the time lock holds (text after JSON escaping, plus file entries).
  const bytes = manifestBytes(text, attached.map(placeholderEntry));
  const fileBytes = attached.reduce((sum, p) => sum + p.bytes.length, 0);
  const fileLimit = me.limits.attachment_bytes;
  const isPrivate = !!recipient || many !== null || newcomer !== null;
  const maxBytes = isPrivate ? MAX_PRIVATE_PLAINTEXT_BYTES : MAX_PLAINTEXT_BYTES;
  // A reply with a fixed time opens exactly when the envelope says (the server checks it).
  const replyFixedAt = reply?.unlock != null ? reply.unlock * 1000 : null;
  const replyExpired = replyFixedAt !== null && replyFixedAt <= now;
  const target = replyFixedAt ?? (preset === "custom" ? new Date(custom).getTime() : now + preset);
  const tooLate = Number.isFinite(target) && target > LATEST_UNLOCK;
  const round = Number.isFinite(target) && target > now && !tooLate ? roundForTime(target) : null;
  const unlockAt = round ? timeForRound(round) : null;
  // The envelope's time for the replies, when fixed.
  const replyTarget = new Date(replyCustom).getTime();
  const envelopeOn = askReply && isPrivate && !reply && recipient !== me.pubkey;
  const replyTooLate = Number.isFinite(replyTarget) && replyTarget > LATEST_UNLOCK;
  const replyRound = Number.isFinite(replyTarget) && replyTarget > now && !replyTooLate ? roundForTime(replyTarget) : null;
  const envelopeOk = !envelopeOn || !replyFixed || replyRound !== null;

  // Private capsules go only to members I follow, and to myself (PLAN.md 4.14; the server enforces it).
  const directory = useMemo(
    () =>
      members.filter(
        (m) => m.pubkey && (m.id === me.id || followed.has(m.id)) && m.display_name.toLowerCase().includes(filter.trim().toLowerCase()),
      ),
    [members, filter, followed, me.id],
  );

  /** Downscales images and reads files as they are chosen, so sizes are known before sending. */
  async function addFiles(list: FileList | null) {
    if (!list) return;
    setError(null);
    setPreparing(true);
    let next = attached;
    for (const file of Array.from(list)) {
      if (next.length >= me.limits.files) {
        setError(t.compose.tooManyFiles(me.limits.files));
        break;
      }
      try {
        const used = next.reduce((sum, p) => sum + p.bytes.length, 0);
        next = [...next, await prepareFile(file, fileLimit - used)];
      } catch (err) {
        setError(errorText(err));
      }
    }
    setAttached(next);
    setPreparing(false);
  }

  // Key-change warning (PLAN.md 4.11 H1): compare the directory's key with the one this browser saw before.
  const recipientMember = recipient ? members.find((m) => m.pubkey === recipient) : undefined;
  const [changedFrom, setChangedFrom] = useState<SeenKey | null>(null);
  useEffect(() => {
    setChangedFrom(null);
    if (!recipientMember?.pubkey || recipientMember.id === me.id) return;
    const current = { pubkey: recipientMember.pubkey, since: recipientMember.key_since };
    void seenKeys.load(me.id, recipientMember.id).then((seen) => {
      if (!seen) void seenKeys.save(me.id, recipientMember.id, current);
      else if (seen.pubkey !== current.pubkey) setChangedFrom(seen);
    });
  }, [recipientMember, me.id]);

  // The same key check for every member of a group send (or the CC list): whose key changed since this browser saw it.
  const groupKeys = many ?? (cc.length > 0 ? cc : null);
  const groupKeysText = groupKeys?.join(",") ?? "";
  useEffect(() => {
    setChangedMany([]);
    setManyKeysChecked(false);
    if (!groupKeys) return;
    void Promise.all(
      groupKeys.map(async (pk) => {
        const m = members.find((x) => x.pubkey === pk);
        if (!m) return null;
        const seen = await seenKeys.load(me.id, m.id);
        if (!seen) await seenKeys.save(me.id, m.id, { pubkey: pk, since: m.key_since });
        return seen && seen.pubkey !== pk ? pk : null;
      }),
    ).then((changed) => setChangedMany(changed.filter((x): x is string => !!x)));
  // `groupKeysText` stands for the list's content.
  }, [groupKeysText, members, me.id]);

  /** The address book is encrypted with the key: opening it asks for the PIN (PLAN.md 4.14). */
  async function loadGroups() {
    setError(null);
    try {
      const stored = await api.addressBook();
      if (!stored.blob) return setBook({ v: 1, groups: [], aliases: {} });
      setBook(await withKey(t.contacts.purposeOpen, async (key) => openAddressBook(stored.blob!, key)));
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
  }

  /** Adds a group's members who are followed and have a key; the others cannot get private capsules. */
  function addGroup(memberIds: number[]) {
    const keys = memberIds
      .filter((id) => followed.has(id))
      .map((id) => members.find((m) => m.id === id)?.pubkey)
      .filter((pk): pk is string => !!pk && pk !== me.pubkey);
    if (many !== null) setMany((prev) => [...new Set([...(prev ?? []), ...keys])].slice(0, MAX_GROUP));
    else setCc((prev) => [...new Set([...prev, ...keys.filter((pk) => pk !== recipient)])].slice(0, MAX_GROUP - 1));
  }
  const toggleCc = (pk: string) => setCc((prev) => (prev.includes(pk) ? prev.filter((x) => x !== pk) : prev.length < MAX_GROUP - 1 ? [...prev, pk] : prev));

  async function acceptKey() {
    if (!recipientMember?.pubkey) return;
    await seenKeys.save(me.id, recipientMember.id, { pubkey: recipientMember.pubkey, since: recipientMember.key_since });
    setChangedFrom(null);
  }

  // A private capsule to a member whose key changed waits until the sender confirmed the new key.
  // The subject is required and never encrypted (PLAN.md 4.14).
  const cleanSubject = normalizeSubject(subject);
  const subjectOk = cleanSubject.length > 0 && [...cleanSubject].length <= MAX_SUBJECT_CHARS;
  // The member's budget in words: daily, or a service account's total (PLAN.md 4.16 K5, 4.18 step 6).
  const budget = t.compose.budget(me.limits.messages, me.usage?.window_seconds === null);
  // Optional, readable before unlock (PLAN.md 4.17); empty means no intro tag.
  const cleanIntro = normalizeIntro(intro);
  const introChars = [...cleanIntro].length;
  // A group goes to everyone selected; the server leaves out whoever blocks me (K9) and says who.
  const manyBlocked = (many ?? []).filter(blocksMe);
  // CC members who block me are left out before sending, so the sealed list names only who gets it.
  const ccBlocked = cc.filter(blocksMe);
  const ccSendable = cc.filter((pk) => !blocksMe(pk) && pk !== recipient);
  const ccOn = !!recipient && !reply && recipient !== me.pubkey;
  // What this send costs (PLAN.md 4.21 D10): one per recipient wrap, two with a reply envelope, plus my copy.
  const wraps = many ? many.length - manyBlocked.length : recipient ? 1 + (ccOn ? ccSendable.length : 0) : 1;
  // PLAN.md 4.24: the author's copy is free. Only a hint: the server decides the cost when it stores the message.
  const cost = estimateStamps({ wraps, envelope: envelopeOn, reply: reply ? { first: reply.count === 0 } : null });
  const balance = me.billing ? me.billing.stamps.remaining : null;
  const shortage = knownShortage(cost, balance);
  /** A service account has no stamps: its cost is part of a technical total. */
  const usesNote = (n: number) => (me.billing ? t.compose.uses(n) : t.compose.usesBudget(n, budget));
  const changeCopy = (on: boolean) => {
    if (!on && !confirm(t.compose.copyOffConfirm)) return;
    setKeepCopy(on);
  };
  /** "Who can read" in the summary before sealing: names of the people, at most three, then a count. */
  const nameList = (pks: string[]) => {
    const names = pks.map((pk) => nameOf(pk));
    return names.length > 3 ? `${names.slice(0, 3).join(", ")} ${t.compose.summaryMore(names.length - 3)}` : names.join(", ");
  };
  const audienceSummary =
    newcomer !== null ? t.compose.summaryNewcomer(newcomer.trim() || "…")
    : many !== null ? (many.length ? nameList(many.filter((pk) => !blocksMe(pk))) : "—")
    : recipient === me.pubkey ? t.compose.summarySelf
    : recipient ? nameList([recipient, ...(ccOn ? ccSendable : [])])
    : t.compose.summaryEveryone;
  const canSend =
    subjectOk &&
    introChars <= MAX_INTRO_CHARS &&
    (!!reply || !(recipient && recipient !== me.pubkey && blocksMe(recipient))) &&
    (!reply || (!!recipient && !replyExpired)) &&
    (many === null || manyBlocked.length < many.length) &&
    (newcomer === null || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newcomer.trim())) &&
    (many === null || (many.length >= 2 && many.length <= MAX_GROUP)) &&
    (groupKeys === null || changedMany.length === 0 || manyKeysChecked) &&
    envelopeOk &&
    !shortage &&
    (text.length > 0 || attached.length > 0) && bytes <= maxBytes && fileBytes <= fileLimit && !!round && !busy && !preparing && !(recipient && changedFrom);

  /**
   * Builds and signs the events once; the server sees nothing until they are complete. Files are
   * uploaded first; the key is asked for only for the signing itself (unlock on demand, 4b step 2g).
   */
  async function build(): Promise<Outgoing> {
    // Count from the click, so a preset like "5 minutes" means five minutes from now.
    const finalRound = replyFixedAt !== null ? roundForTime(replyFixedAt) : preset === "custom" ? round! : roundForTime(Date.now() + preset);
    // The envelope (PLAN.md 4.21): the replies' round, or null for the recipient's choice.
    const sase = envelopeOn ? { round: replyFixed ? roundForTime(replyTarget) : null } : undefined;
    const named = (pk: string) => ({ pubkey: pk, display_name: members.find((m) => m.pubkey === pk)?.display_name ?? nameOf(pk) });
    // Sender snapshot (PLAN.md 4.10): the identity's profile now, signed into the capsule.
    const profile = me.profile;
    const sender = profile ? { display_name: profile.display_name, name: profile.name ?? undefined, about: profile.about ?? undefined } : undefined;
    // Each file: encrypted with its own key and uploaded; only its key goes into the time lock.
    const files: ManifestFile[] = [];
    for (const p of attached) files.push(await uploadFile(p));
    const blobs = files.map((f) => f.sha256);
    const note = recipient ? { recipient, round: finalRound, createdAt: Math.floor(Date.now() / 1000) } : null;
    const built = await withKey(t.keyDialog.purposes.send, async (secretKey) => {
      const opts = { subject: cleanSubject, plaintext: text, files, round: finalRound, secretKey, chainClient: chainClient(), sender, ...(cleanIntro ? { intro: cleanIntro } : {}), ...(isPrivate && discreet ? { discreet } : {}), ...(sase ? { sase } : {}), ...(reply ? { replyTo: reply.id } : {}) };
      if (newcomer !== null) {
        // Only my own copy goes to the server now; I pass it on when the new member asks.
        return { events: [await sealPrivate({ ...opts, recipientPublicKey: me.pubkey! })], note: null, blobs, newcomer: newcomer.trim() };
      }
      if (many) {
        const { wraps, copy } = await sealPrivateGroup({ ...opts, recipientPublicKeys: many, copy: keepCopy });
        return { events: copy ? [...wraps, copy] : wraps, note: null, blobs, group: { copy: !!copy } };
      }
      if (recipient && ccOn && ccSendable.length > 0) {
        // CC: one capsule to the recipient and everyone in CC, the list signed inside the seal (PLAN.md 4.21).
        const keys = [recipient, ...ccSendable];
        const { wraps, copy } = await sealPrivateGroup({ ...opts, recipientPublicKeys: keys, copy: keepCopy, recipients: { to: [named(recipient)], cc: ccSendable.map(named) } });
        return { events: copy ? [...wraps, copy] : wraps, note: null, blobs, group: { copy: !!copy } };
      }
      // One member: the sealed list names them alone, so they see they are the only recipient (PLAN.md 4.21 D8).
      const single = { ...opts, recipientPublicKey: recipient, ...(ccOn ? { recipients: { to: [named(recipient)], cc: [] } } : {}) };
      if (recipient && keepCopy && recipient !== me.pubkey) {
        const { wrap, copy } = await sealPrivateWithCopy(single);
        return { events: [wrap, copy], note, blobs };
      }
      if (recipient) return { events: [await sealPrivate(single)], note, blobs };
      return { events: [await sealPublic(opts)], note: null, blobs };
    });
    return { ...built, round: finalRound };
  }

  /** What the confirmation after sending says (PLAN.md 4.31): who, when, and a capsule the author can keep. */
  function sealedNotice(out: Outgoing, rejected: number, on: string): Sealed {
    const self = !out.newcomer && !out.group && recipient === me.pubkey;
    const copy = !isPrivate || self || out.newcomer ? out.events[0] : out.group ? (out.group.copy ? out.events[out.events.length - 1] : null) : out.events.length === 2 ? out.events[1] : null;
    const audience: Sealed["audience"] = !isPrivate ? { kind: "public" } : self ? { kind: "self" } : { kind: "people", count: Math.max(1, wraps - rejected) };
    return { on, unlockAt: timeForRound(out.round), audience, copy: copy ?? null, files: out.blobs.length > 0 };
  }

  async function send() {
    if (!canSend || !round) return;
    setError(null);
    setBillingHint(false);
    setBusy(true);
    let out = outgoing;
    let rejected: string[] = [];
    try {
      if (!out) {
        out = await build();
        setOutgoing(out);
      }
      if (out.newcomer) {
        await api.createPending({ email: out.newcomer, copy: out.events[0], blobs: out.blobs, lang });
      } else if (out.group) {
        const wraps = out.group.copy ? out.events.slice(0, -1) : out.events;
        rejected = (await api.submitGroup(wraps, out.group.copy ? out.events[out.events.length - 1] : null, out.blobs)).rejected ?? [];
      } else if (out.events.length === 2) await api.submitPair(out.events[0], out.events[1], out.blobs);
      else await api.submit(out.events[0], out.blobs);
    } catch (err) {
      if (isKeyRequestCancelled(err)) {
        setBusy(false);
        return;
      }
      if (err instanceof ApiError && ["no_stamps", "storage_quota", "blob_too_large"].includes(err.code ?? "")) {
        // Show the server's answer, not an estimate; the balance on screen is refreshed to the real one.
        setBillingHint(true);
        void refresh().catch(() => undefined);
      }
      if (!out) setError(explain(err));
      else if (err instanceof ApiError) setError(t.compose.notDelivered(errorText(err)));
      else setError(t.compose.unconfirmed);
      setBusy(false);
      return;
    }
    dropDraft(draft);
    announceSealed(sealedNotice(out, rejected.length, reply || envelopeOn ? "/correspondence" : "/sent"));
    // Local notes only after the server accepted the capsule.
    if (out.note && out.events.length === 1) await sentPrivate.save(me.id, out.events[0].id, out.note);
    // The key was unchanged or confirmed: remember it as the one this browser has seen.
    if (recipientMember?.pubkey && recipientMember.id !== me.id) {
      await seenKeys.save(me.id, recipientMember.id, { pubkey: recipientMember.pubkey, since: recipientMember.key_since });
    }
    // Submission already succeeded: a failed quota refresh must never offer to resend it.
    void refresh().catch(() => undefined);
    if (rejected.length > 0) {
      setLeftOut(rejected);
      setLeftOutOfCc(!!out.group && ccOn);
      return;
    }
    if (reply) navigate(`/correspondence?tab=received&message=${encodeURIComponent(reply.id)}`);
    else if (envelopeOn) navigate("/correspondence?tab=sent");
    else navigate("/sent");
  }

  /** Keys of the group or CC list that changed since this browser saw them: confirm before sending. */
  const changedNotice = changedMany.length > 0 && (
    <div className="warn small">
      <p>{t.compose.manyKeysChanged(changedMany.map((pk) => nameOf(pk)).join(", "))}</p>
      <label className="radio">
        <input
          type="checkbox"
          checked={manyKeysChecked}
          onChange={(e) => {
            setManyKeysChecked(e.target.checked);
            if (e.target.checked)
              for (const pk of changedMany) {
                const m = members.find((x) => x.pubkey === pk);
                if (m) void seenKeys.save(me.id, m.id, { pubkey: pk, since: m.key_since });
              }
          }}
        />{" "}
        {t.compose.manyKeysChecked}
      </label>
    </div>
  );
  /** The address-book groups as chips, for the group and the CC pickers. */
  const groupChips = book === null ? (
    <button type="button" className="small" onClick={() => void loadGroups()} disabled={busy}>
      {t.compose.loadGroups}
    </button>
  ) : (
    <div className="chips">
      {book.groups.length === 0 && <span className="small muted">{t.contacts.noGroups}</span>}
      {book.groups.map((g) => (
        <button key={g.id} type="button" className="chip" onClick={() => addGroup(g.members)} disabled={busy}>
          + {g.name}
        </button>
      ))}
    </div>
  );

  if (me.pubkey === null) return <p className="muted">{t.compose.noKeyYet} <a href="/keys" onClick={link("/keys")}>{t.app.noKeyLink}</a>.</p>;
  if (keyStatus === "none" || keyStatus === "mismatch") return <p className="muted">{t.compose.needKey}</p>;

  return (
    <div className="compose-page">
      <div className="page-head"><h1>{reply ? t.compose.replyTitle : t.compose.title}</h1><p className="muted">{reply ? t.compose.replyNote : t.design.composeNote}</p>{!reply && <p className="small"><a href="/try" onClick={link("/try")}>{t.tryIt.accountLink}</a>{t.tryIt.accountNote}</p>}</div>
      {!targetAvailable && <p className="warn small">{t.design.targetUnavailable}</p>}
      {restored && drafted && (
        <p className="note small" role="status">
          {t.compose.draftRestored}{" "}
          <button type="button" className="link small" onClick={discardDraft} disabled={busy}>{t.compose.draftDiscard}</button>
        </p>
      )}
      <div className="compose">
        <div className="content">
          {/* Outside the capsule: what the recipient (and Vrelis) can read before the opening. */}
          <div className="field">
            <div className="field-head"><label htmlFor="compose-subject">{t.compose.subject}</label><span className="field-badge"><Icon name="eye" size={14} />{t.compose.visibleBefore}</span></div>
            <p className="field-hint" id="compose-subject-hint">{t.design.subjectHint}</p>
            <input id="compose-subject" aria-describedby="compose-subject-hint" dir="auto" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} required />
          </div>
          <div className="field">
            <div className="field-head"><label htmlFor="compose-intro">{t.compose.intro}</label><span className={`field-count ${introChars > MAX_INTRO_CHARS ? "error" : ""}`}>{t.compose.introCount(formatNumber(introChars), formatNumber(MAX_INTRO_CHARS))}</span></div>
            <p className="field-hint" id="compose-intro-hint">{t.compose.introHint}</p>
            <textarea id="compose-intro" aria-describedby="compose-intro-hint" className="intro-input" dir="auto" rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} disabled={busy} />
          </div>

          {/* Inside the capsule: text and files, sealed in this browser until the opening. */}
          <section className={busy ? "sealed-block sealing" : "sealed-block"} aria-labelledby="compose-sealed-title">
            {/* A dash that circles the edge while the capsule is being sealed (PLAN.md 4.33). Decoration only. */}
            <svg className="seal-orbit" aria-hidden="true"><rect width="100%" height="100%" rx="14" pathLength="100" /></svg>
            <span className="seal-badge" aria-hidden="true"><SealIcon /></span>
            <div className="sealed-head">
              <h2 id="compose-sealed-title"><Icon name="lock" size={16} />{t.compose.sealedTitle}</h2>
              {unlockAt && round && <span className="sealed-until">{t.compose.sealedUntil(formatTime(unlockAt))}</span>}
            </div>
            <p className="field-hint">{t.compose.sealedNote}</p>
            <label htmlFor="compose-text" className="visually-hidden">{t.compose.message}</label>
            <textarea id="compose-text" dir="auto" rows={8} value={text} onChange={(e) => setText(e.target.value)} disabled={busy} />
            <div className="sealed-foot">
              <label className={`file-button ${busy || preparing || attached.length >= me.limits.files ? "disabled" : ""}`}>
                <Icon name="paperclip" size={16} />{t.compose.addFiles}
                <input type="file" className="visually-hidden" multiple onChange={(e) => void addFiles(e.target.files).then(() => (e.target.value = ""))} disabled={busy || preparing || attached.length >= me.limits.files} />
              </label>
              <span className={`field-count ${bytes > maxBytes ? "error" : ""}`}>{t.compose.bytes(formatNumber(bytes), formatNumber(maxBytes))}</span>
            </div>
            {attached.length > 0 && (
              <ul className="sealed-files">
                {attached.map((p, i) => (
                  <li key={i}>
                    <Icon name="paperclip" size={14} />{cleanFileName(p.name)} <span className="muted">{formatBytes(p.bytes.length)}</span>
                    {p.shrunk && <span className="muted">{t.compose.madeSmaller}</span>}
                    <button type="button" className="icon-button" aria-label={`${t.common.remove} ${cleanFileName(p.name)}`} title={t.common.remove} onClick={() => setAttached(attached.filter((_, j) => j !== i))} disabled={busy}><Icon name="close" size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            <p className={`field-hint ${fileBytes > fileLimit ? "error" : ""}`}>
              {preparing ? t.compose.preparing : ""}
              {t.compose.filesNote(formatBytes(fileBytes), formatBytes(fileLimit), me.limits.files)}
              {me.billing && <> <a href="/billing" onClick={link("/billing")}>{t.compose.moreSpace}</a></>}
            </p>
          </section>
        </div>

        <div className="side">
          <fieldset>
            <legend><Icon name="watching" size={16} />{t.compose.opensIn}</legend>
            {replyFixedAt !== null ? (
              <p className={replyExpired ? "error small" : "small"}>{replyExpired ? t.compose.replyExpired : t.compose.replyTimeFixed(formatTime(replyFixedAt))}</p>
            ) : reply ? <p className="small muted">{t.compose.replyTimeFree}</p> : null}
            {replyFixedAt === null && <div className="chips">
              {PRESETS.map(([label, ms]) => (
                <button key={label} type="button" className={preset === ms ? "chip on" : "chip"} aria-pressed={preset === ms} onClick={() => setPreset(ms)} disabled={busy}>
                  {t.compose.presets[label]}
                </button>
              ))}
              <button type="button" className={preset === "custom" ? "chip on" : "chip"} aria-pressed={preset === "custom"} onClick={() => setPreset("custom")} disabled={busy}>
                {t.compose.pickDate}
              </button>
            </div>}
            {replyFixedAt === null && preset === "custom" && (
              <input type="datetime-local" aria-label={t.compose.pickDate} value={custom} max={toLocalInput(LATEST_UNLOCK)} onChange={(e) => setCustom(e.target.value)} disabled={busy} />
            )}
            {unlockAt && round ? (
              <div><p className="time-summary">{t.design.opensAt(formatTime(unlockAt))}</p><details className="small muted timing-details"><summary>{t.design.timingDetails}</summary><p>{t.compose.opensAt(formatTime(unlockAt), formatNumber(round))}</p></details></div>
            ) : replyExpired ? null : (
              <p className="error small">{tooLate ? t.compose.tooLate : t.compose.inPast}</p>
            )}
          </fieldset>

          {reply && (
            <fieldset className="reply-target">
              <legend><Icon name="people" size={16} />{t.compose.whoCanRead}</legend>
              {recipient ? (
                <>
                  <p>{t.compose.replyTo(nameOf(recipient))} <code>{fingerprint(recipient)}</code></p>
                  <p className="small muted">{t.compose.replyOnlyToSender}</p>
                  <p className="small muted">{reply.count === 0 ? t.compose.replyFirstCost() : t.compose.replyLaterCost()}</p>
                  <Disclosure title={t.design.options}>
                    <label className="radio">
                      <input type="checkbox" checked={keepCopy} onChange={(e) => changeCopy(e.target.checked)} disabled={busy} /> {t.compose.keepCopy}
                    </label>
                    <p className="muted small">{keepCopy ? t.compose.copyOn(nameOf(recipient)) : t.compose.copyOff(nameOf(recipient))}</p>
                    <label className="radio">
                      <input type="checkbox" checked={discreet} onChange={(e) => setDiscreet(e.target.checked)} disabled={busy} /> {t.compose.discreet}
                    </label>
                    <p className="muted small">{t.compose.discreetNote}</p>
                  </Disclosure>
                </>
              ) : (
                <p className={replyLookup === "pending" ? "small muted" : "warn small"}>{replyLookup === "pending" ? t.common.loading : t.compose.replyUnavailable}</p>
              )}
            </fieldset>
          )}

          {!reply && <fieldset>
            <legend><Icon name="people" size={16} />{t.compose.whoCanRead}</legend>
            <div className="chips" role="radiogroup" aria-label={t.compose.whoCanRead}>
              <button
                type="button"
                role="radio"
                aria-checked={!recipient && many === null && newcomer === null}
                className={!recipient && many === null && newcomer === null ? "chip on" : "chip"}
                onClick={() => {
                  setRecipient("");
                  setMany(null);
                  setNewcomer(null);
                }}
                disabled={busy}
              >
                {t.compose.everyone}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!!recipient}
                className={recipient ? "chip on" : "chip"}
                onClick={() => {
                  if (recipient) return;
                  setMany(null);
                  setNewcomer(null);
                  setRecipient(directory.find((m) => m.pubkey !== me.pubkey)?.pubkey ?? directory[0]?.pubkey ?? "");
                }}
                disabled={busy || directory.length === 0}
              >
                {t.compose.oneMember(recipient ? nameOf(recipient) : null)}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={many !== null}
                className={many !== null ? "chip on" : "chip"}
                onClick={() => {
                  if (many !== null) return;
                  setRecipient("");
                  setNewcomer(null);
                  setMany([]);
                }}
                disabled={busy || followed.size < 2}
              >
                {t.compose.severalMembers(many?.length ?? 0)}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={newcomer !== null}
                className={newcomer !== null ? "chip on" : "chip"}
                onClick={() => {
                  if (newcomer !== null) return;
                  setRecipient("");
                  setMany(null);
                  setNewcomer("");
                }}
                disabled={busy}
              >
                {t.compose.newcomer}
              </button>
            </div>
            {recipient && (
              <div className="directory">
                <input type="search" aria-label={t.compose.search} placeholder={t.compose.search} value={filter} onChange={(e) => setFilter(e.target.value)} disabled={busy} />
                {followed.size === 0 && <p className="small muted">{t.compose.followFirst}</p>}
                <ul>
                  {directory.map((m) => (
                    <li key={m.id}>
                      <button type="button" className={m.pubkey === recipient ? "chip on" : "chip"} aria-pressed={m.pubkey === recipient} disabled={busy} onClick={() => setRecipient(m.pubkey!)}>
                        {m.display_name}
                        {m.pubkey === me.pubkey ? t.compose.youMark : ""} <span className="muted small">{fingerprint(m.pubkey!)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {/* The fingerprint stays visible: comparing it is how a member checks the recipient's key. */}
                <p className="small">
                  {t.compose.keyOf(nameOf(recipient))}
                  <code>{fingerprint(recipient)}</code>
                  {recipientMember?.key_since ? <span className="muted">{t.compose.since(formatTime(recipientMember.key_since * 1000))}</span> : null}
                </p>
                <details className="small recipient-key"><summary>{t.compose.fullKey(nameOf(recipient))}</summary>
                  <code className="select">{publicKeyToNpub(recipient)}</code>
                </details>
                {changedFrom && (
                  <div className="warn small">
                    {t.compose.keyChanged(
                      nameOf(recipient),
                      recipientMember?.key_since ? formatTime(recipientMember.key_since * 1000) : null,
                      fingerprint(changedFrom.pubkey),
                      fingerprint(recipient),
                    )}
                    <button type="button" className="small" onClick={() => void acceptKey()}>
                      {t.compose.checked(nameOf(recipient))}
                    </button>
                  </div>
                )}
                {recipient !== me.pubkey && blocksMe(recipient) && <p className="warn small">{t.compose.blockedOne(nameOf(recipient))}</p>}
                {recipient === me.pubkey && <p className="muted small">{t.compose.toSelf}</p>}
                {ccOn && (
                  <Disclosure title={<>{t.compose.cc}{cc.length > 0 && <span className="tag">{cc.length}</span>}</>}>
                    <p className="small muted">{t.compose.ccNote(MAX_GROUP - 1)}</p>
                    {groupChips}
                    <ul>
                      {directory
                        .filter((m) => m.pubkey !== me.pubkey && m.pubkey !== recipient)
                        .map((m) => {
                          const on = cc.includes(m.pubkey!);
                          return (
                            <li key={m.id}>
                              <button type="button" className={on ? "chip on" : "chip"} aria-pressed={on} disabled={busy || (!on && cc.length >= MAX_GROUP - 1)} onClick={() => toggleCc(m.pubkey!)}>
                                {(book?.aliases[String(m.id)] ?? "") || m.display_name}
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                    {ccBlocked.length > 0 && <p className="warn small">{t.compose.blockedMany(ccBlocked.map((pk) => nameOf(pk)).join(", "))}</p>}
                    {changedNotice}
                  </Disclosure>
                )}
                {recipient !== me.pubkey && (
                  <Disclosure title={t.design.options}>
                    <label className="radio">
                      <input type="checkbox" checked={keepCopy} onChange={(e) => changeCopy(e.target.checked)} disabled={busy} /> {t.compose.keepCopy}
                    </label>
                    <p className="muted small">{keepCopy ? t.compose.copyOn(nameOf(recipient)) : t.compose.copyOff(nameOf(recipient))}</p>
                    <label className="radio">
                      <input type="checkbox" checked={discreet} onChange={(e) => setDiscreet(e.target.checked)} disabled={busy} /> {t.compose.discreet}
                    </label>
                    <p className="muted small">{t.compose.discreetNote}</p>
                  </Disclosure>
                )}
              </div>
            )}
            {newcomer !== null && (
              <div className="directory">
                <label>
                  {t.login.password.email}
                  <input type="email" value={newcomer} onChange={(e) => setNewcomer(e.target.value)} disabled={busy} />
                </label>
                <p className="small muted">{t.compose.newcomerNote}</p>
              </div>
            )}
            {many !== null && (
              <div className="directory">
                <p className="small muted">{t.compose.severalNote(MAX_GROUP)}</p>
                {groupChips}
                <input type="search" aria-label={t.compose.search} placeholder={t.compose.search} value={filter} onChange={(e) => setFilter(e.target.value)} disabled={busy} />
                <ul>
                  {directory
                    .filter((m) => m.pubkey !== me.pubkey)
                    .map((m) => {
                      const on = many.includes(m.pubkey!);
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            className={on ? "chip on" : "chip"}
                            aria-pressed={on}
                            disabled={busy || (!on && many.length >= MAX_GROUP)}
                            onClick={() =>
                              setMany((prev) => {
                                const list = prev ?? [];
                                return list.includes(m.pubkey!) ? list.filter((pk) => pk !== m.pubkey) : list.length < MAX_GROUP ? [...list, m.pubkey!] : list;
                              })
                            }
                          >
                            {(book?.aliases[String(m.id)] ?? "") || m.display_name}
                          </button>
                        </li>
                      );
                    })}
                </ul>
                {manyBlocked.length > 0 && (
                  <div className="warn small">
                    <p>{t.compose.blockedMany(manyBlocked.map((pk) => nameOf(pk)).join(", "))}</p>
                    {manyBlocked.length === many.length && <p>{t.compose.groupAllBlocked}</p>}
                  </div>
                )}
                {changedNotice}
                <Disclosure title={t.design.options}>
                <label className="radio">
                  <input type="checkbox" checked={keepCopy} onChange={(e) => changeCopy(e.target.checked)} disabled={busy} /> {t.compose.keepCopy}
                </label>
                <p className="muted small">{me.billing ? t.compose.groupCost(cost) : usesNote(cost)}</p>
                <label className="radio">
                  <input type="checkbox" checked={discreet} onChange={(e) => setDiscreet(e.target.checked)} disabled={busy} /> {t.compose.discreet}
                </label>
                <p className="muted small">{t.compose.discreetNote}</p>
                </Disclosure>
              </div>
            )}
          </fieldset>}

          {isPrivate && !reply && recipient !== me.pubkey && (
            <fieldset className="envelope-fieldset">
              <legend><Icon name="sent" size={16} />{t.compose.envelope}</legend>
              <label className="radio">
                <input type="checkbox" checked={askReply} onChange={(e) => setAskReply(e.target.checked)} disabled={busy} /> {t.compose.envelopeAsk}
              </label>
              <p className="small muted">{t.compose.envelopeIntro}</p>
              <details className="envelope-help small muted"><summary>{t.compose.envelopeDetails}</summary><p>{t.compose.envelopeNote}</p></details>
              {askReply && (
                <div className="envelope-time">
                  <div className="chips" role="radiogroup" aria-label={t.compose.envelope}>
                    <button type="button" role="radio" aria-checked={replyFixed} className={replyFixed ? "chip on" : "chip"} onClick={() => setReplyFixed(true)} disabled={busy}>{t.compose.envelopeFixed}</button>
                    <button type="button" role="radio" aria-checked={!replyFixed} className={!replyFixed ? "chip on" : "chip"} onClick={() => setReplyFixed(false)} disabled={busy}>{t.compose.envelopeFree}</button>
                  </div>
                  {replyFixed && (
                    <>
                      <input type="datetime-local" aria-label={t.compose.envelopeFixed} value={replyCustom} max={toLocalInput(LATEST_UNLOCK)} onChange={(e) => setReplyCustom(e.target.value)} disabled={busy} />
                      {replyRound ? <p className="time-summary">{t.compose.envelopeOpens(formatTime(timeForRound(replyRound)))}</p> : <p className="error small">{replyTooLate ? t.compose.tooLate : t.compose.inPast}</p>}
                      <p className="small muted">{t.compose.envelopeDeadline}</p>
                    </>
                  )}
                  <p className="small muted">{me.billing ? t.compose.envelopeCost(cost) : usesNote(cost)}</p>
                </div>
              )}
            </fieldset>
          )}

          {/* What sealing will do, just before the button (PLAN.md 4.31): who, when, what is readable before the opening, the cost. */}
          <section className="seal-summary" aria-labelledby="seal-summary-title">
            <h2 id="seal-summary-title">{t.compose.summaryTitle}</h2>
            <dl>
              <dt>{t.compose.summaryTo}</dt>
              <dd>{audienceSummary}</dd>
              <dt>{t.compose.summaryOpens}</dt>
              <dd>{unlockAt ? formatTime(unlockAt) : "—"}</dd>
              {envelopeOn && (
                <>
                  <dt>{t.compose.summaryReplies}</dt>
                  <dd>{replyFixed ? (replyRound ? formatTime(timeForRound(replyRound)) : "—") : t.compose.envelopeFree}</dd>
                </>
              )}
              <dt>{isPrivate ? t.compose.summaryVisiblePrivate : t.compose.summaryVisiblePublic}</dt>
              <dd dir="auto">
                {cleanSubject ? <q>{cleanSubject}</q> : "—"}
                {cleanIntro && <span className="seal-summary-intro"><q>{cleanIntro}</q></span>}
              </dd>
              <dt>{t.compose.summarySealed}</dt>
              <dd>{t.compose.summaryText(formatNumber([...text].length))}{attached.length > 0 && <> · {t.compose.summaryFiles(formatNumber(attached.length))}</>}</dd>
              {me.billing && (
                <>
                  <dt>{t.compose.summaryCost}</dt>
                  <dd>{cost === 0 ? t.compose.summaryFree : t.billing.badge(cost)}</dd>
                </>
              )}
            </dl>
          </section>

          <div className="send">
            {leftOut && (
              <div className="warn small">
                <p>{t.compose.leftOut(leftOut.map((pk) => nameOf(pk)).join(", "))}</p>
                {leftOutOfCc && <p>{t.compose.ccLeftOut}</p>}
                <button type="button" className="small" onClick={() => navigate(envelopeOn ? "/correspondence?tab=sent" : "/sent")}>{envelopeOn ? t.envelopes.title : t.compose.toSent}</button>
              </div>
            )}
            <button className="primary" disabled={!canSend || leftOut !== null} onClick={send}>
              <Icon name="sent" size={17} />
              {busy ? t.compose.sealing : outgoing && error ? t.compose.sendAgain : t.compose.seal}
            </button>
            {me.billing && balance !== null && (
              <p className={shortage ? "error small" : "small muted"} role={shortage ? "alert" : undefined}>
                {shortage ? t.compose.stampShort(cost, balance) : t.compose.stampStatus(cost, balance)}{" "}
                {t.compose.stampRule}{" "}
                <a href="/billing" onClick={link("/billing")}>{t.compose.seeBilling}</a>
              </p>
            )}
            {error && <p className="error">{error}{billingHint && <> <a href="/billing" onClick={link("/billing")}>{t.compose.seeBilling}</a></>}</p>}
            {drafted && <p className="small muted">{t.compose.draftKept}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** An open padlock with a small clock: the capsule is open while it is written and locks at the chosen time. */
function SealIcon() {
  return (
    <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 22v-7a8 8 0 0 1 15.5-2.8" />
      <rect x="8" y="22" width="24" height="18" rx="3" />
      <path d="M20 29v4" />
      <circle cx="35" cy="33" r="9" fill="var(--surface)" />
      <path d="M35 28v5l3 2" />
    </svg>
  );
}
